import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { supabase } from "./lib/supabase";
import { C } from "./constants/colors";
import { OPPORTUNITIES, ARTISTS } from "./constants/demoData";
import { css } from "./constants/globalStyles";
import { useBreakpoint } from "./hooks/useBreakpoint";
import { mapOpportunity, mapArtist } from "./lib/utils";
import { LoginScreen } from "./pages/LoginScreen";
import { SignUpScreen } from "./pages/SignUpScreen";
import { ArtistMini } from "./pages/ArtistDashboard";
import { GalleryMini } from "./pages/GalleryDashboard";
import { PublicSite } from "./pages/PublicSite";
import WaitlistPage from "./pages/WaitlistPage";
import ArtistApplicationDetail from "./pages/ArtistApplicationDetail";
import SubmitOpportunity from "./pages/SubmitOpportunity";
import ResetPasswordPage from "./pages/ResetPasswordPage";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/*
  opencall. — Supabase-Connected App
  ────────────────────────────────────
  Auth:        supabase.auth (sign up, log in, log out, session persistence)
  Data:        opportunities and artist profiles fetched from Supabase
  Storage:     portfolio images uploaded to the portfolio-images bucket
  Applications: submitted to the applications table
*/





/* ══════════════════════════
   ROOT — CONNECTED APP
══════════════════════════ */
export default function App() {
  const [user, setUser] = useState(undefined); // undefined=loading, null=unauthed, object=authed

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Subdomain routing — after hooks so rules of hooks are satisfied
  if (window.location.hostname === 'waitlist.opencall.com.au') {
    return <WaitlistPage />;
  }

  if (user === undefined) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.parch }}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: ".7rem", color: C.muted, letterSpacing: "2px", textTransform: "uppercase" }}>Loading…</span>
        </div>
      </>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <BrowserRouter>
        <Routes>
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/submit" element={<SubmitOpportunity />} />
          <Route path="*" element={
            user ? <AuthenticatedRoutes /> : <Navigate to="/waitlist" replace />
          } />
        </Routes>
      </BrowserRouter>
    </Elements>
  );
}

function LoginRoute() {
  const navigate = useNavigate();
  return (
    <>
      <style>{css}</style>
      <LoginScreen
        onLogin={() => navigate('/')}
        onSignup={() => navigate('/signup')}
      />
    </>
  );
}

function SignupRoute() {
  const navigate = useNavigate();
  return (
    <>
      <style>{css}</style>
      <SignUpScreen
        defaultType="artist"
        onSignup={() => navigate('/')}
        onLogin={() => navigate('/login')}
      />
    </>
  );
}

function AuthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/applications/:id" element={<ArtistApplicationDetail />} />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}

function MainApp() {
  const bp = useBreakpoint();
  // mode: "loading" | "public" | "login" | "signup-artist" | "signup-gallery" | "artist" | "gallery"
  const [mode, setMode] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [opportunities, setOpportunities] = useState(OPPORTUNITIES);
  const [artists, setArtists] = useState(ARTISTS);
  const fetchingProfile = useRef(false);

  const fetchProfile = async (userId, userMeta) => {
    // Prevent concurrent calls — onAuthStateChange can fire multiple events rapidly
    if (fetchingProfile.current) return;
    fetchingProfile.current = true;
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (data) {
        setProfile(data);
        setMode(data.user_type);
        return;
      }
      // Profile row missing — DB trigger may not have run yet. Try to create it.
      const userType = userMeta?.user_type || 'artist';
      const fullName = userMeta?.full_name || '';
      const galleryTrialFields = userType === 'gallery' ? {
        plan: 'established',
        trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        subscription_status: 'trialing',
      } : {};
      const { data: newProfile } = await supabase
        .from('profiles')
        .insert({ id: userId, user_type: userType, full_name: fullName, ...galleryTrialFields })
        .select()
        .single();
      if (newProfile) {
        setProfile(newProfile);
        setMode(newProfile.user_type);
        return;
      }
      // INSERT failed (trigger likely created the row already) — retry SELECT
      const { data: retryProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (retryProfile) {
        setProfile(retryProfile);
        setMode(retryProfile.user_type);
      } else {
        console.error('Could not load profile for', userId);
        setMode("public");
      }
    } finally {
      fetchingProfile.current = false;
    }
  };

  const fetchPublicData = () => {
    supabase
      .from('opportunities')
      .select('*')
      .eq('status', 'active')
      .order('deadline', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) setOpportunities(data.map(mapOpportunity));
      });

    supabase
      .from('profiles')
      .select('*, portfolio_images(image_url, sort_order)')
      .eq('user_type', 'artist')
      .limit(20)
      .then(({ data }) => {
        if (data && data.length > 0) setArtists(data.map(mapArtist));
      });
  };

  useEffect(() => {
    fetchPublicData();

    // Auth state listener — fires INITIAL_SESSION on load, then on login/logout/token refresh
    // Handles both session persistence on refresh and new logins in one place.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        fetchProfile(session.user.id, session.user.user_metadata);
        fetchPublicData(); // refresh opportunities/artists on every login
      } else if (event === "INITIAL_SESSION") {
        setMode("public");
      } else {
        setProfile(null);
        setMode("public");
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    // onAuthStateChange listener above handles the mode change
  };

  if (mode === "loading") {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.parch }}>
          <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".7rem",color:C.muted,letterSpacing:"2px",textTransform:"uppercase" }}>Loading…</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      {mode==="public"&&(
        <PublicSite
          bp={bp}
          opportunities={opportunities}
          artists={artists}
          currentProfile={null}
          onLogin={()=>setMode("login")}
          onSignupArtist={()=>setMode("signup-artist")}
          onSignupGallery={()=>setMode("signup-gallery")}
        />
      )}
      {mode==="login"&&(
        <LoginScreen
          onLogin={(type)=>setMode(type)}
          onSignup={()=>setMode("signup-artist")}
        />
      )}
      {(mode==="signup-artist"||mode==="signup-gallery")&&(
        <SignUpScreen
          defaultType={mode==="signup-gallery"?"gallery":"artist"}
          onSignup={(type)=>setMode(type)}
          onLogin={()=>setMode("login")}
        />
      )}
      {mode==="artist"&&(
        <ArtistMini
          bp={bp}
          profile={profile}
          opportunities={opportunities}
          onPublic={()=>setMode("public")}
          onLogout={handleLogout}
        />
      )}
      {mode==="gallery"&&(
        <GalleryMini
          bp={bp}
          profile={profile}
          onPublic={()=>setMode("public")}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
